// frontend/src/pages/AutomationMode.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Globe, 
  Play, 
  Pause, 
  Square, 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Download,
  Settings,
  Zap,
  Clock,
  Target,
  List,
  Image,
  FileText,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Activity,
  Wifi,
  WifiOff,
  Terminal,
  Code,
  Layers
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'

import { useWebSocket } from '@hooks/useWebSocket'
import { useBrowserSession } from '@hooks/useBrowserSession'
import { useAuth } from '@contexts/AuthContext'
import TestRunner from '@components/automation/TestRunner'
import StepViewer from '@components/automation/StepViewer'
import ScreenshotGallery from '@components/automation/ScreenshotGallery'
import ProgressBar from '@components/automation/ProgressBar'
import TestResults from '@components/automation/TestResults'
import Button from '@components/common/Button'
import Input from '@components/common/Input'



const AutomationMode = () => {
  const [url, setUrl] = useState('')
  const [command, setCommand] = useState('')
  const [testMode, setTestMode] = useState('full') // 'full' or 'command'
  const [testCases, setTestCases] = useState([])
  const [currentTest, setCurrentTest] = useState(null)
  const [executionState, setExecutionState] = useState('idle') // idle, generating, running, completed, failed
  const [screenshots, setScreenshots] = useState([])
  const [testResults, setTestResults] = useState([])
  const [executionLog, setExecutionLog] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [selectedTestCase, setSelectedTestCase] = useState(null)
  const [showTestDetails, setShowTestDetails] = useState(false)
  const [activeTestRun, setActiveTestRun] = useState(null)

  // ✅ NEW: Stop execution state management
  const [isStoppingExecution, setIsStoppingExecution] = useState(false)

  // New state for responsive panels
  const [collapsedPanels, setCollapsedPanels] = useState({
    testCases: false,
    execution: false,
    screenshots: false,
    results: false
  })

  const { sendMessage, isConnected, addMessageHandler } = useWebSocket()
  const { createSession, activeSession } = useBrowserSession()
  const { user, isAuthenticated } = useAuth()

  // Sample URLs for quick testing
  // const sampleUrls = [
  //   'https://example.com',
  //   'https://httpbin.org',
  //   'https://jsonplaceholder.typicode.com',
  //   'https://reqres.in'
  // ]

  // Sample commands for quick testing
  // const sampleCommands = [
  //   'Click on the login button',
  //   'Fill in the contact form',
  //   'Search for "automation testing"',
  //   'Navigate to the about page',
  //   'Check if the footer is visible',
  //   'Verify the page title'
  // ]
  const handleGenerateTestCases = async () => {
  //    if (!isAuthenticated || !user || !user._id) {
  //   toast.error('User authentication required. Please login again.')
  //   return
  // }

    if (!url.trim()) {
      toast.error('Please enter a URL')
      return
    }

    if (testMode === 'command' && !command.trim()) {
      toast.error('Please enter a command for command-based testing')
      return
    }

    if (!isAuthenticated || !user) {
      toast.error('Please login to create test runs')
      return
    }

    try {
      new URL(url.trim())
    } catch {
      toast.error('Please enter a valid URL (including http:// or https://)')
      return
    }

    if (!isConnected) {
      toast.error('WebSocket not connected. Please refresh the page.')
      return
    }

    try {
      console.log(`Starting ${testMode} test generation for URL:`, url.trim())
      console.log('Current user:', user)
      
      // Clear previous state
      setTestCases([])
      setTestResults([])
      setScreenshots([])
      setExecutionLog([])
      setSelectedTestCase(null)
      setActiveTestRun(null) 
      setExecutionState('generating')

      // Create session in backend
      const sessionName = testMode === 'command' 
        ? `Command Test: ${command}` 
        : `Automation Test: ${url}`
      
      const session = await createSession('automation', sessionName, {
        url: url.trim(),
        description: testMode === 'command' 
          ? `Command-based testing: ${command}`
          : `Full automation testing of ${url}`,
        settings: {
          testMode,
          command: testMode === 'command' ? command.trim() : undefined,
          url: url.trim()
        }
      })

      console.log('✅ Session created in backend:', session)

      // ✅ FIXED: Create test run with correct field names
      const testRunData = {
        session_id: session.id,
        user_id: user._id || user.id,
        url: url.trim(), // ✅ Use url instead of target_url
        target_url: url.trim(), // ✅ Use target_url instead of url
        test_suite_name: testMode === 'command' 
          ? `Command Test: ${command}` 
          : `Automation Test: ${url}`,
        test_cases: [], // Empty initially
        status: 'pending', // ✅ Use lowercase to match enum
        total_tests: 0
      }

      console.log('Creating test run with data:', testRunData)

      const testRun = await api.createTestRun(testRunData)
      setActiveTestRun(testRun)
      console.log('✅ Test run created:', testRun)

      // Continue with WebSocket test generation...
      const messageType = testMode === 'command' ? 'command_generate_test_cases' : 'generate_test_cases'
      const messageData = testMode === 'command' 
        ? { url: url.trim(), command: command.trim() }
        : { url: url.trim() }

      const success = sendMessage({
        type: messageType,
        data: {
          ...messageData,
          session_id: session.id
        }
      })

      if (!success) {
        throw new Error('Failed to send WebSocket message')
      }

      console.log(`${messageType} message sent with session ID:`, session.id)
      
    } catch (error) {
      console.error('Error in handleGenerateTestCases:', error)
      setExecutionState('failed')
      toast.error('Failed to generate test cases: ' + error.message)
    }
  }

  // ✅ FIXED: Stop execution with proper debouncing
  const handleStopExecution = useCallback(() => {
    // Prevent multiple calls
    // if (isStoppingExecution) {
    //   console.log('⚠️ Stop execution already in progress, ignoring duplicate call')
    //   return
    // }

    if (!isConnected) {
      toast.error('WebSocket not connected')
      return
    }

    console.log('🛑 Stopping execution - setting flag to prevent duplicates')
    setIsStoppingExecution(true)

    // Send stop message
const success = sendMessage({
  type: 'stop_execution',
  data: {
    stopId: `stop_${Date.now()}`,
    force: isStoppingExecution,
    session_id: activeSession?.id,
    user_id: user?._id || user?.id,
    timestamp: Date.now()
  }
})

    if (success) {
      console.log('✅ Stop execution message sent successfully')
      toast.success('Stopping execution...', { duration: 2000 })
    } else {
      console.error('❌ Failed to send stop execution message')
      setIsStoppingExecution(false) // Reset flag on failure
      toast.error('Failed to send stop command')
    }

    // Reset the flag after a delay to allow future stops
    setTimeout(() => {
      setIsStoppingExecution(false)
      console.log('🔓 Stop execution flag reset')
    }, 3000) // 3 second delay to prevent spam

  }, [isConnected, sendMessage, isStoppingExecution])

  // ✅ ENHANCED: WebSocket message handler with proper execution state management
  const handleWebSocketMessage = useCallback((message) => {
    console.log('🟢 Handling WebSocket message:', message)
    
    switch (message.type) {
      case 'generation_started':
        setExecutionState('generating')
        setIsStoppingExecution(false) // Reset stop flag
        toast.loading(testMode === 'command' ? 'Generating command-based test...' : 'Generating test cases...', { id: 'generating' })
        break

      case 'progress_update':
        toast.loading(message.data.message, { id: 'generating' })
        break

      case 'test_cases_generated':
        console.log('📨 Received test cases generated message:', message.data)
        if (!message.data) {
          console.error('❌ No data property in message:', message)
          break;
        }
        if (!Array.isArray(message.data.test_cases)) {
          console.error('❌ test_cases is not an array:', message.data.test_cases)
        } else {
          console.log('✅ test_cases array received, length:', message.data.test_cases.length)
        }
        let testCasesRaw = message.data.test_cases;
        if (!Array.isArray(testCasesRaw)) {
          if (testCasesRaw && typeof testCasesRaw === 'object') {
            // If it's a single object, wrap it in an array
            testCasesRaw = [testCasesRaw];
            console.warn('⚠️ test_cases was an object, wrapped in array:', testCasesRaw);
          } else {
            // If it's null, undefined, or not an object, set to empty array
            testCasesRaw = [];
            console.error('❌ test_cases is not an array or object:', message.data.test_cases);
          }
        }
        const normalizedTestCases = (testCasesRaw).map((testCase, index) => {
          // Accept both snake_case and camelCase field names
          return {
            id: String(index + 1),
            test_name: testCase.test_name || testCase.testName || testCase.name,
            name: testCase.test_name || testCase.testName || testCase.name, // For compatibility
            description: testCase.description || testCase.test_description,
            steps: testCase.steps || testCase.test_steps || [],
            category: testCase.category || testCase.test_category,
            priority: testCase.priority || testCase.test_priority,
            expected_result: testCase.expected_result || testCase.expectedResult || testCase.expected_results,
            url: testCase.url || testCase.test_url,
            command: testCase.command || testCase.test_command
          }
        })
        console.log('🔍 Raw test cases from backend:', message.data.test_cases)
        console.log('🔍 Normalized test cases:', normalizedTestCases)
        setTestCases(normalizedTestCases)
        setExecutionState('idle')
        setIsStoppingExecution(false)
        toast.dismiss('generating')
        toast.success(`Generated ${normalizedTestCases.length} test case${normalizedTestCases.length !== 1 ? 's' : ''} for ${testMode} testing`)
        break

      case 'execution_started':
        setExecutionState('running')
        setIsStoppingExecution(false) // Reset stop flag when execution starts
        setTestResults([])
        setScreenshots([])
        setExecutionLog([])
        setProgress({ current: 0, total: message.data.total_tests })
        toast.success('Test execution started!')
        break

      // ✅ NEW: Handle execution stopping acknowledgment
      case 'execution_stopping':
        console.log('📨 Received execution stopping acknowledgment')
        toast.loading('Stopping execution...', { id: 'stopping', duration: 3000 })
        break

      case 'test_progress':
        setProgress({
          current: message.data.current_test,
          total: message.data.total_tests
        })
        setCurrentTest(message.data.test_name)
        break

      case 'step_update':
        const stepData = message.data.step_data
        setExecutionLog(prev => [...prev, stepData])
        
        // ✅ Debug logging for screenshot data
        console.log('📊 Step update received:', {
          has_screenshot: !!stepData.screenshot,
          screenshot_keys: stepData.screenshot ? Object.keys(stepData.screenshot) : [],
          screenshot_data_exists: !!(stepData.screenshot?.base64_data || stepData.screenshot?.data || stepData.screenshot?.image_data),
          data_length: (stepData.screenshot?.base64_data || stepData.screenshot?.data || stepData.screenshot?.image_data)?.length || 0,
          step_description: stepData.step_description || stepData.description
        })
        
        // ✅ Save individual step screenshots immediately (matching your backend format)
        if (stepData.screenshot && activeTestRun) {
          const screenshotData = {
            test_run_id: activeTestRun.id,
            test_case_name: currentTest || 'Unknown Test',
            step_name: stepData.step_description || stepData.description || 'Test step', // Your backend expects "step_name"
            step_index: stepData.step_index || 0,
            image_data: stepData.screenshot.image_data || stepData.screenshot.base64_data || stepData.screenshot.data, // Your backend expects "image_data"
            width: stepData.screenshot.width,
            height: stepData.screenshot.height,
            metadata: {
              test_step: stepData.step_description || stepData.description,
              timestamp: new Date().toISOString()
            }
          }
          
          console.log('📸 Attempting to save screenshot with correct backend format:', {
            test_run_id: screenshotData.test_run_id,
            test_case_name: screenshotData.test_case_name,
            has_image_data: !!screenshotData.image_data,
            data_length: screenshotData.image_data?.length || 0
          })
          
          // Save step screenshot to database using your existing backend
          api.createScreenshot(screenshotData)
            .then(result => {
              if (result.error) {
                console.error('❌ Screenshot save failed:', result.error)
              } else {
                console.log('✅ Screenshot saved successfully:', result)
              }
            })
            .catch(error => {
              console.error('❌ Screenshot save error:', error)
            })
        } else {
          console.log('⚠️ No screenshot data or active test run:', {
            has_screenshot: !!stepData.screenshot,
            has_active_test_run: !!activeTestRun,
            active_test_run_id: activeTestRun?.id,
            screenshot_data_available: !!(stepData.screenshot?.base64_data || stepData.screenshot?.data)
          })
        }
        
        // Add to screenshots state for UI display
        if (stepData.screenshot) {
          setScreenshots(prev => [...prev, stepData.screenshot])
        }
        break

      case 'test_completed':
  const result = message.data.result
  setTestResults(prev => [...prev, result])
  
  // ✅ FIXED: Ensure activeTestRun exists and has ID before saving
  if (activeTestRun && activeTestRun.id) {
    console.log('💾 Saving test result with test_run_id:', activeTestRun.id)
    
    // Process screenshots if they exist
    const processedScreenshots = result.screenshots || []
    const processedLogs = result.logs || []
    
    // Save each screenshot if API route exists
    if (result.screenshots && result.screenshots.length > 0) {
      Promise.all(
        result.screenshots.map(async (screenshot, index) => {
          try {
            if (screenshot.base64_data || screenshot.data || screenshot.image_data) {
              // Save screenshot using your existing API route with correct format
              const savedScreenshot = await api.createScreenshot({
                test_run_id: activeTestRun.id, // ✅ Required field
                test_case_name: message.data.test_name,
                step_name: screenshot.step_description || `Final Screenshot ${index + 1}`,
                step_index: index,
                image_data: screenshot.base64_data || screenshot.data || screenshot.image_data,
                width: screenshot.width,
                height: screenshot.height,
                metadata: {
                  test_completion: true,
                  timestamp: screenshot.timestamp || new Date().toISOString()
                }
              })
              return savedScreenshot
            }
            return screenshot
          } catch (error) {
            console.error('Failed to save screenshot:', error)
            return screenshot
          }
        })
      ).then(savedScreenshots => {
        console.log('✅ Screenshots saved:', savedScreenshots.length)
      })
    }
    
    // ✅ FIXED: Include required test_run_id in createTestResult call
    const testResultData = {
      test_run_id: activeTestRun.id, // ✅ REQUIRED FIELD - was missing!
      test_case_name: message.data.test_name,
      status: result.status,
      execution_time: result.execution_time,
      error_message: result.error,
      screenshots: processedScreenshots,
      logs: processedLogs
    }
    
    console.log('🚀 Creating test result with data:', testResultData)
    
    api.createTestResult(testResultData)
      .then(savedResult => {
        console.log('✅ Test result saved successfully:', savedResult)
      })
      .catch(error => {
        console.error('❌ Failed to save test result:', error)
        // ✅ More detailed error logging
        if (error.message.includes('test_run_id')) {
          console.error('💥 test_run_id missing! activeTestRun:', activeTestRun)
        }
      })
    
    // ✅ ALSO: Update the test run's screenshots_paths and execution_log
    if (result.screenshots || result.logs) {
      const screenshotPaths = result.screenshots?.map(s => s.path || s.url).filter(Boolean) || []
      const logEntries = result.logs?.map(log => ({
        timestamp: new Date().toISOString(),
        level: 'info',
        message: log
      })) || []
      
      api.updateTestRun(activeTestRun.id, {
        screenshots_paths: screenshotPaths,
        execution_log: logEntries
      }).catch(error => {
        console.error('Failed to update test run with screenshots/logs:', error)
      })
    }
  } else {
    // ✅ Better error handling when activeTestRun is missing
    console.error('❌ Cannot save test result - activeTestRun missing or invalid:', {
      activeTestRun_exists: !!activeTestRun,
      activeTestRun_id: activeTestRun?.id,
      test_name: message.data.test_name,
      result_status: result.status
    })
    toast.error('Cannot save test result - no active test run')
  }
  
  toast.success(`Test "${message.data.test_name}" ${result.status}`, {
    icon: result.status === 'passed' ? '✅' : '❌'
  })
  break

      case 'test_failed':
  setTestResults(prev => [...prev, message.data])

  // ✅ FIXED: Ensure activeTestRun exists and has ID before saving
  if (activeTestRun && activeTestRun.id) {
    console.log('💾 Saving failed test result with test_run_id:', activeTestRun.id)
    
    // Process screenshots for failed tests too
    if (message.data.screenshots && message.data.screenshots.length > 0) {
      Promise.all(
        message.data.screenshots.map(async (screenshot, index) => {
          try {
            if (screenshot.base64_data || screenshot.data || screenshot.image_data) {
              const savedScreenshot = await api.createScreenshot({
                test_run_id: activeTestRun.id, // ✅ Required field
                test_case_name: message.data.test_name,
                step_name: screenshot.step_description || `Failed Test Screenshot ${index + 1}`,
                step_index: index,
                image_data: screenshot.base64_data || screenshot.data || screenshot.image_data,
                width: screenshot.width,
                height: screenshot.height,
                metadata: {
                  test_failed: true,
                  error: message.data.error,
                  timestamp: screenshot.timestamp || new Date().toISOString()
                }
              })
              return savedScreenshot
            }
            return screenshot
          } catch (error) {
            console.error('Failed to save failed test screenshot:', error)
            return screenshot
          }
        })
      ).then(savedScreenshots => {
        console.log('✅ Failed test screenshots saved:', savedScreenshots.length)
      })
    }
    
    // ✅ FIXED: Include required test_run_id in createTestResult call
    const failedTestResultData = {
      test_run_id: activeTestRun.id, // ✅ REQUIRED FIELD - was missing!
      test_case_name: message.data.test_name,
      status: 'failed',
      error_message: message.data.error,
      execution_time: message.data.execution_time || 0,
      screenshots: message.data.screenshots || [],
      logs: message.data.logs || []
    }
    
    console.log('🚀 Creating failed test result with data:', failedTestResultData)
    
    api.createTestResult(failedTestResultData)
      .then(savedResult => {
        console.log('✅ Failed test result saved successfully:', savedResult)
      })
      .catch(error => {
        console.error('❌ Failed to save failed test result:', error)
        if (error.message.includes('test_run_id')) {
          console.error('💥 test_run_id missing! activeTestRun:', activeTestRun)
        }
      })
  } else {
    // ✅ Better error handling when activeTestRun is missing
    console.error('❌ Cannot save failed test result - activeTestRun missing or invalid:', {
      activeTestRun_exists: !!activeTestRun,
      activeTestRun_id: activeTestRun?.id,
      test_name: message.data.test_name,
      error: message.data.error
    })
    toast.error('Cannot save failed test result - no active test run')
  }
  
  toast.error(`Test failed: ${message.data.error}`)
  break
      case 'all_tests_completed':
  setExecutionState('completed')
  setCurrentTest(null)
  setIsStoppingExecution(false) // ✅ Reset stop flag when tests complete
  
  // ✅ FIXED: Update test run with correct field names and status
  if (activeTestRun && activeTestRun.id) {
    console.log('💾 Updating test run completion with test_run_id:', activeTestRun.id)
    
    const completionData = {
      status: 'completed',
      completed_at: new Date().toISOString(),
      execution_time_seconds: message.data.execution_time || 0,
      total_tests: message.data.total_tests || testResults.length,
      passed_tests: message.data.passed || testResults.filter(r => r.status === 'passed').length,
      failed_tests: (message.data.total_tests || testResults.length) - (message.data.passed || testResults.filter(r => r.status === 'passed').length),
      test_results: testResults || []
    }
    
    console.log('🚀 Updating test run with completion data:', completionData)
    
    api.updateTestRun(activeTestRun.id, completionData)
      .then(updatedTestRun => {
        console.log('✅ Test run updated successfully:', updatedTestRun)
      })
      .catch(error => {
        console.error('❌ Failed to update test run:', error)
        // ✅ More detailed error logging
        if (error.message.includes('test_run_id')) {
          console.error('💥 test_run_id issue! activeTestRun:', activeTestRun)
        }
      })
  } else {
    // ✅ Better error handling when activeTestRun is missing
    console.error('❌ Cannot update test run completion - activeTestRun missing or invalid:', {
      activeTestRun_exists: !!activeTestRun,
      activeTestRun_id: activeTestRun?.id,
      total_tests: message.data.total_tests,
      passed_tests: message.data.passed
    })
    toast.error('Cannot update test run - no active test run')
  }
  
  toast.success(`All tests completed! ${message.data.passed}/${message.data.total_tests} passed`, {
    duration: 6000,
    icon: '🎉'
  })
  break

      case 'execution_stopped':
  setExecutionState('idle')
  setCurrentTest(null)
  setIsStoppingExecution(false) // ✅ Reset stop flag when execution actually stops
  toast.dismiss('stopping') // Dismiss stopping toast
  
  // ✅ FIXED: Update test run with proper validation
  if (activeTestRun && activeTestRun.id) {
    console.log('💾 Updating stopped test run with test_run_id:', activeTestRun.id)
    
    const stoppedData = {
      status: 'failed', // Using 'failed' since 'stopped' is not in your enum
      completed_at: new Date().toISOString(),
      execution_time_seconds: message.data.execution_time || 0,
      total_tests: testResults.length,
      passed_tests: testResults.filter(r => r.status === 'passed').length,
      failed_tests: testResults.filter(r => r.status === 'failed').length,
      execution_log: [{
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Test execution was stopped by user'
      }]
    }
    
    console.log('🚀 Updating stopped test run with data:', stoppedData)
    
    api.updateTestRun(activeTestRun.id, stoppedData)
      .then(updatedTestRun => {
        console.log('✅ Stopped test run updated successfully:', updatedTestRun)
      })
      .catch(error => {
        console.error('❌ Failed to update stopped test run:', error)
        if (error.message.includes('test_run_id')) {
          console.error('💥 test_run_id issue! activeTestRun:', activeTestRun)
        }
      })
  } else {
    // ✅ Better error handling when activeTestRun is missing
    console.error('❌ Cannot update stopped test run - activeTestRun missing or invalid:', {
      activeTestRun_exists: !!activeTestRun,
      activeTestRun_id: activeTestRun?.id,
      current_results: testResults.length
    })
    toast.error('Cannot update stopped test run - no active test run')
  }
  
  toast.success('Test execution stopped successfully!')
  break

     case 'error':
  setExecutionState('failed')
  setIsStoppingExecution(false) // ✅ Reset stop flag on error
  toast.dismiss('generating')
  toast.dismiss('stopping') // Dismiss stopping toast if active
  
  // ✅ FIXED: Update test run with proper validation
  if (activeTestRun && activeTestRun.id) {
    console.log('💾 Updating failed test run with test_run_id:', activeTestRun.id)
    
    const errorData = {
      status: 'error',
      completed_at: new Date().toISOString(),
      execution_time_seconds: 0,
      total_tests: testCases.length || 0,
      passed_tests: 0,
      failed_tests: testCases.length || 0,
      execution_log: [{ 
        timestamp: new Date().toISOString(),
        level: 'error',
        message: message.data.message || 'Unknown error occurred'
      }]
    }
    
    console.log('🚀 Updating error test run with data:', errorData)
    
    api.updateTestRun(activeTestRun.id, errorData)
      .then(updatedTestRun => {
        console.log('✅ Error test run updated successfully:', updatedTestRun)
      })
      .catch(error => {
        console.error('❌ Failed to update error test run:', error)
        if (error.message.includes('test_run_id')) {
          console.error('💥 test_run_id issue! activeTestRun:', activeTestRun)
        }
      })
  } else {
    // ✅ Better error handling when activeTestRun is missing
    console.error('❌ Cannot update error test run - activeTestRun missing or invalid:', {
      activeTestRun_exists: !!activeTestRun,
      activeTestRun_id: activeTestRun?.id,
      error_message: message.data.message
    })
    toast.error('Cannot update error test run - no active test run')
  }
  
  toast.error(message.data.message || 'An error occurred during test execution')
  break

      default:
        console.log('Unhandled message type:', message.type)
    }
  }, [testMode, activeTestRun, currentTest, testResults])

  // Register WebSocket message handler
  useEffect(() => {
    if (isConnected) {
      const unsubscribe = addMessageHandler(handleWebSocketMessage)
      return unsubscribe
    }
  }, [isConnected, addMessageHandler,])

  

  const handleRunAllTests = async () => {
    if (testCases.length === 0) {
      toast.error('No test cases to run')
      return
    }

    if (!isConnected) {
      toast.error('WebSocket not connected. Please refresh the page.')
      return
    }

    if (!activeSession) {
      toast.error('No active session. Please generate test cases first.')
      return
    }

    if (!isAuthenticated || !user) {
      toast.error('Please login to run tests')
      return
    }

    try {
      console.log('Starting test execution for', testCases.length, 'test cases')
      
      // ✅ FIXED: Transform test cases to match backend TestCase model
      const transformedTestCases = testCases.map((testCase, index) => ({
        id: String(index + 1), // ✅ Keep as string as requested
        name: testCase.test_name || testCase.name,
        description: testCase.description,
        category: testCase.category || 'General',
        priority: testCase.priority?.toLowerCase() || 'medium', // lowercase for enum
        steps: testCase.steps || [],
        expected_result: testCase.expectedResults || testCase.expected_results || ""
      }))

      const testRunData = {
        session_id: activeSession.id,
        user_id: user._id || user.id,
        url: url.trim(), // ✅ Use url instead of target_url
        target_url: url.trim(), // ✅ Use target_url
        test_suite_name: testMode === 'command' 
          ? `Command Test: ${command}` 
          : `Automation Test: ${url}`,
        test_cases: transformedTestCases,
        status: 'running', // ✅ Use lowercase
        started_at: new Date().toISOString(),
        total_tests: testCases.length
      }
      
      const testRun = await api.createTestRun(testRunData)
      setActiveTestRun(testRun)
      console.log('✅ Test run created and started:', testRun)

      const success = sendMessage({
        type: 'run_all_tests',
        data: {
          url: url.trim(),
          test_cases: testCases,
          test_mode: testMode,
          command: testMode === 'command' ? command.trim() : undefined,
          session_id: activeSession.id,
          test_run_id: testRun.id
        }
      })

      if (!success) {
        throw new Error('Failed to send WebSocket message')
      }

      console.log('✅ Test execution started with session ID:', activeSession.id)
      
    } catch (error) {
      console.error('Error in handleRunAllTests:', error) 
      setExecutionState('failed')
      toast.error('Failed to start test execution: ' + error.message)
    }
  }

  const handleRunSingleTest = async (testCase) => {
    if (!isConnected) {
      toast.error('WebSocket not connected. Please refresh the page.')
      return
    }

    if (!activeSession) {
      toast.error('No active session. Please generate test cases first.')
      return
    }

    if (!isAuthenticated || !user) {
      toast.error('Please login to run tests')
      return
    }

    try {
      setSelectedTestCase(testCase)
      
      // ✅ FIXED: Transform single test case correctly
      const transformedTestCase = {
        id: "1", // ✅ Keep as string as requested
        name: testCase.test_name || testCase.name,
        description: testCase.description,
        category: testCase.category || 'General',
        priority: testCase.priority?.toLowerCase() || 'medium', // lowercase
        steps: testCase.steps || [],
        expected_result: testCase.expectedResults || testCase.expected_results || ""
      }

      const testRunData = {
        session_id: activeSession.id,
        user_id: user._id || user.id,
        url: url.trim(),
        target_url: url.trim(), // ✅ Use target_url
        test_suite_name: `Single Test: ${testCase.test_name}`,
        test_cases: [transformedTestCase],
        status: 'running', // ✅ Use lowercase
        started_at: new Date().toISOString(),
        total_tests: 1
      }
      
      const testRun = await api.createTestRun(testRunData)
      setActiveTestRun(testRun)
      console.log('✅ Single test run created:', testRun)
      
      const success = sendMessage({
        type: 'run_single_test',
        data: {
          url: url.trim(),
          test_case: testCase,
          test_mode: testMode,
          command: testMode === 'command' ? command.trim() : undefined,
          session_id: activeSession.id,
          test_run_id: testRun.id
        }
      })

      if (!success) {
        throw new Error('Failed to send WebSocket message')
      }

      toast.success(`Running test: ${testCase.test_name}`)
      
    } catch (error) {
      console.error('Error in handleRunSingleTest:', error)
      toast.error('Failed to run test: ' + error.message)
    }
  }

  const handleClearResults = () => {
    setTestResults([])
    setScreenshots([])
    setExecutionLog([])
    setSelectedTestCase(null)
    setProgress({ current: 0, total: 0 })
    toast.success('Results cleared')
  }

  const handleExportResults = () => {
    const exportData = {
      url,
      command: testMode === 'command' ? command : null,
      testMode,
      timestamp: new Date().toISOString(),
      testCases,
      testResults,
      screenshots: screenshots.map(s => ({ ...s, base64: null })), // Remove base64 for file size
      summary: {
        total: testResults.length,
        passed: testResults.filter(r => r.status === 'passed').length,
        failed: testResults.filter(r => r.status === 'failed').length,
        executionTime: testResults.reduce((acc, r) => acc + (r.execution_time || 0), 0)
      }
    }

    const dataStr = JSON.stringify(exportData, null, 2)
    const dataBlob = new Blob([dataStr], {type: 'application/json'})
    const url_obj = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url_obj
    link.download = `test-results-${testMode}-${new Date().toISOString().split('T')[0]}.json`
    link.click()
    URL.revokeObjectURL(url_obj)
    
    toast.success('Results exported successfully')
  }

  const togglePanel = (panel) => {
    setCollapsedPanels(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }))
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'text-emerald-600'
      case 'failed': return 'text-red-600'
      case 'running': return 'text-blue-600'
      case 'pending': return 'text-amber-600'
      default: return 'text-gray-600'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return CheckCircle
      case 'failed': return XCircle
      case 'running': return RefreshCw
      case 'pending': return Clock
      default: return AlertCircle
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': case 'critical': return 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-300'
      case 'medium': return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border-orange-300'
      case 'low': return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-300'
      default: return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border-gray-300'
    }
  }

  const getExecutionStateMessage = () => {
    switch (executionState) {
      case 'generating': 
        return testMode === 'command' 
          ? 'Analyzing website and generating command-based test...'
          : 'Analyzing website and generating test cases...'
      case 'running': return currentTest ? `Running: ${currentTest}` : 'Executing tests...'
      case 'completed': return 'All tests completed successfully!'
      case 'failed': return 'Test execution failed'
      default: return `Ready to generate ${testMode === 'command' ? 'command-based' : 'comprehensive'} test cases`
    }
  }
  useEffect(() => {
  window.automationState = {
    setIsStoppingExecution,
    setExecutionState
  }
  window.toast = toast
  
  return () => {
    delete window.automationState
    delete window.toast
  }
}, [])

  return (
    // SIMPLIFIED CONTAINER - No height constraints, works with Layout
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-400 to-purple-600 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.1, 1, 1.1],
            rotate: [360, 180, 0],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-pink-400 to-red-500 rounded-full blur-3xl"
        />
      </div>

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/70 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-lg"
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-3 lg:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <motion.div 
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xl"
            >
              {testMode === 'command' ? (
                <Terminal className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
              ) : (
                <Zap className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
              )}
            </motion.div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                Automation Mode
                {testMode === 'command' && (
                  <span className="ml-2 text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                    Command-based
                  </span>
                )}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600">
                {testMode === 'command' 
                  ? 'Execute specific commands on websites'
                  : 'Generate and run comprehensive website tests'
                }
              </p>
            </div>
          </div>
          
          {/* Status Indicators */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto">
            {/* User indicator */}
            {isAuthenticated && user && (
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 px-2 sm:px-3 py-1 sm:py-2 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl border border-gray-200/70 shadow-sm text-xs sm:text-sm"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="font-medium text-gray-700 truncate max-w-20">
                  {user.username || user.email || 'User'}
                </span>
              </motion.div>
            )}
            
            {/* Test Mode Indicator */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2 px-2 sm:px-3 py-1 sm:py-2 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl border border-gray-200/70 shadow-sm text-xs sm:text-sm"
            >
              {testMode === 'command' ? (
                <Code className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
              ) : (
                <Layers className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
              )}
              <span className="font-medium text-gray-700">
                {testMode === 'command' ? 'Command Mode' : 'Full Testing'}
              </span>
            </motion.div>

            {/* Connection Status */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2 px-2 sm:px-3 py-1 sm:py-2 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl border border-gray-200/70 shadow-sm text-xs sm:text-sm"
            >
              {isConnected ? (
                <Wifi className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
              ) : (
                <WifiOff className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
              )}
              <span className="font-medium text-gray-700">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </motion.div>
            
            {/* Execution Status */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2 px-2 sm:px-3 py-1 sm:py-2 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl border border-gray-200/70 shadow-sm text-xs sm:text-sm flex-1 lg:flex-none"
            >
              <motion.div 
                animate={executionState === 'running' || executionState === 'generating' ? 
                  { scale: [1, 1.2, 1], opacity: [0.7, 1, 0.7] } : 
                  { scale: 1, opacity: 1 }
                }
                transition={{ duration: 1, repeat: Infinity }}
                className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
                  executionState === 'running' ? 'bg-blue-500' :
                  executionState === 'generating' ? 'bg-amber-500' :
                  executionState === 'completed' ? 'bg-emerald-500' :
                  executionState === 'failed' ? 'bg-red-500' :
                  'bg-gray-400'
                }`} 
              />
              <span className="font-medium text-gray-700 truncate">
                {getExecutionStateMessage()}
              </span>
            </motion.div>

            {/* Stats */}
            {(testCases.length > 0 || testResults.length > 0) && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center space-x-2"
              >
                <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                  {testCases.length} tests
                </div>
                {testResults.length > 0 && (
                  <div className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full font-medium">
                    {testResults.filter(r => r.status === 'passed').length} passed
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Auth warning for non-authenticated users */}
      {!isAuthenticated && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-40 bg-amber-50 border-b border-amber-200 px-4 py-3"
        >
          <div className="flex items-center justify-center space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Please <span className="font-semibold">login</span> to create and run automation tests
            </p>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <div className="relative z-10 p-3 sm:p-4 lg:p-6 pb-20">
        <div className="w-full">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 sm:gap-4 lg:gap-6">
            
            {/* Left Panel - Test Configuration & Test Cases */}
            <div className="xl:col-span-4 space-y-3 sm:space-y-4 lg:space-y-6">
              {/* Test Mode Selection & URL Input Section */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white/90 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-gray-200/70 shadow-xl p-3 sm:p-4 lg:p-6"
              >
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                    <Globe className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
                    Test Configuration
                  </h3>
                </div>
                
                <div className="space-y-4 sm:space-y-5">
                  {/* Test Mode Toggle */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Test Mode</label>
                    <div className="flex bg-gray-100 rounded-xl p-1">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setTestMode('full')}
                        disabled={executionState === 'running' || executionState === 'generating'}
                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          testMode === 'full'
                            ? 'bg-white text-blue-700 shadow-sm border border-blue-200'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <Layers className="h-4 w-4" />
                        <span>Full Testing</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setTestMode('command')}
                        disabled={executionState === 'running' || executionState === 'generating'}
                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          testMode === 'command'
                            ? 'bg-white text-orange-700 shadow-sm border border-orange-200'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <Terminal className="h-4 w-4" />
                        <span>Command-based</span>
                      </motion.button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {testMode === 'full' 
                        ? 'Generate comprehensive test cases covering various aspects of the website'
                        : 'Execute specific commands or test scenarios on the website'
                      }
                    </p>
                  </div>

                  {/* URL Input */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                    <Globe className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mt-3" />
                    <Input
                      type="url"
                      placeholder="https://example.com"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="pl-10 sm:pl-12 h-10 sm:h-12 border-2 rounded-lg sm:rounded-xl bg-white/70 backdrop-blur-sm transition-all duration-200 focus:bg-white hover:bg-white/90 text-sm sm:text-base"
                      disabled={executionState === 'running' || executionState === 'generating'}
                    />
                  </div>

                  {/* Command Input (only visible in command mode) */}
                  <AnimatePresence>
                    {testMode === 'command' && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="relative"
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-2">Command</label>
                        <Code className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mt-3" />
                        <textarea
                          placeholder="Describe what you want to test or do on the website..."
                          value={command}
                          onChange={(e) => setCommand(e.target.value)}
                          className="w-full pl-10 sm:pl-12 pr-4 py-3 border-2 rounded-lg sm:rounded-xl bg-white/70 backdrop-blur-sm transition-all duration-200 focus:bg-white hover:bg-white/90 text-sm sm:text-base resize-none"
                          disabled={executionState === 'running' || executionState === 'generating'}
                          rows={3}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Example: "Click on the login button and verify the form appears"
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Quick URL Selection */}
                  {/* <div className="grid grid-cols-2 gap-2">
                    {sampleUrls.map((sampleUrl) => (
                      <motion.button
                        key={sampleUrl}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setUrl(sampleUrl)}
                        disabled={executionState === 'running' || executionState === 'generating'}
                        className="px-2 sm:px-3 py-2 text-xs bg-gradient-to-r from-gray-100 to-gray-200 hover:from-blue-100 hover:to-indigo-100 text-gray-700 hover:text-blue-700 rounded-lg transition-all duration-200 disabled:opacity-50 font-medium border border-gray-200 hover:border-blue-300"
                      >
                        {sampleUrl.replace('https://', '')}
                      </motion.button>
                    ))}
                  </div> */}

                  {/* Quick Command Selection (only visible in command mode) */}
                  <AnimatePresence>
                    {testMode === 'command' && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-2"
                      >
                        {/* <label className="block text-sm font-medium text-gray-700">Quick Commands</label>
                        <div className="grid grid-cols-1 gap-2">
                          {sampleCommands.map((sampleCommand) => (
                            <motion.button
                              key={sampleCommand}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setCommand(sampleCommand)}
                              disabled={executionState === 'running' || executionState === 'generating'}
                              className="px-3 py-2 text-xs bg-gradient-to-r from-orange-100 to-amber-100 hover:from-orange-200 hover:to-amber-200 text-orange-700 hover:text-orange-800 rounded-lg transition-all duration-200 disabled:opacity-50 font-medium border border-orange-200 hover:border-orange-300 text-left"
                            >
                              {sampleCommand}
                            </motion.button>
                          ))}
                        </div> */}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  <Button
                    onClick={handleGenerateTestCases}
                    disabled={
                      !url.trim() || 
                      (testMode === 'command' && !command.trim()) ||
                      executionState === 'generating' || 
                      executionState === 'running' || 
                      !isConnected ||
                      !isAuthenticated
                    }
                    className={`w-full h-10 sm:h-12 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 ${
                      testMode === 'command' 
                        ? 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                    }`}
                    variant="primary"
                  >
                    {executionState === 'generating' ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        </motion.div>
                        {testMode === 'command' ? 'Generating Command Test...' : 'Generating Tests...'}
                      </>
                    ) : (
                      <>
                        {testMode === 'command' ? (
                          <Terminal className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        ) : (
                          <Settings className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        )}
                        {testMode === 'command' ? 'Generate Command Test' : 'Generate Test Cases'}
                      </>
                    )}
                  </Button>

                  {/* Connection Warning */}
                  {!isConnected && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl"
                    >
                      <div className="flex items-center space-x-3">
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                        </motion.div>
                        <div>
                          <p className="text-xs sm:text-sm font-semibold text-amber-800">WebSocket Disconnected</p>
                          <p className="text-xs text-amber-700 mt-1">Please refresh the page to reconnect</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>

              {/* Test Cases List */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/90 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-gray-200/70 shadow-xl"
              >
                <div className="p-3 sm:p-4 lg:p-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                      <List className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
                      Generated Test Cases
                      {testMode === 'command' && (
                        <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                          Command-based
                        </span>
                      )}
                    </h3>
                    {testCases.length > 0 && (
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          testMode === 'command' 
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {testCases.length} tests
                        </span>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowTestDetails(!showTestDetails)}
                          className={`text-xs font-medium px-2 py-1 rounded-lg transition-all duration-200 ${
                            testMode === 'command'
                              ? 'text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100'
                              : 'text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100'
                          }`}
                        >
                          {showTestDetails ? (
                            <>
                              <ChevronUp className="h-3 w-3 inline mr-1" />
                              Hide
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3 inline mr-1" />
                              Show
                            </>
                          )}
                        </motion.button>
                      </div>
                    )}
                  </div>
                  
                  {/* Test Cases Container */}
                  <div className="space-y-3">
                    {testCases.length === 0 ? (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center text-gray-500 py-8 sm:py-12"
                      >
                        <motion.div
                          animate={{ y: [0, -5, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center ${
                            testMode === 'command'
                              ? 'bg-gradient-to-r from-orange-100 to-amber-100'
                              : 'bg-gradient-to-r from-blue-100 to-purple-100'
                          }`}
                        >
                          {testMode === 'command' ? (
                            <Terminal className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" />
                          ) : (
                            <Target className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                          )}
                        </motion.div>
                        <p className="font-semibold text-sm sm:text-lg mb-1 sm:mb-2">No test cases generated yet</p>
                        <p className="text-xs sm:text-sm">
                          {testMode === 'command' 
                            ? 'Enter a URL and command, then click "Generate Command Test"'
                            : 'Enter a URL and click "Generate Test Cases"'
                          }
                        </p>
                      </motion.div>
                    ) : (
                      testCases.map((testCase, index) => {
                        const result = testResults.find(r => r.test_case?.id === testCase.id)
                        const StatusIcon = getStatusIcon(result?.status || 'pending')
                        const isSelected = selectedTestCase?.id === testCase.id
                        
                        return (
                          <motion.div
                            key={testCase.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ scale: 1.01 }}
                            className={`border-2 rounded-lg sm:rounded-xl p-3 sm:p-4 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg ${
                              isSelected 
                                ? testMode === 'command'
                                  ? 'border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50 shadow-md'
                                  : 'border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md'
                                : 'border-gray-200 bg-white/70 hover:bg-white hover:border-gray-300'
                            }`}
                            onClick={() => setSelectedTestCase(isSelected ? null : testCase)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                                  <motion.div
                                    animate={result?.status === 'running' ? { rotate: 360 } : { rotate: 0 }}
                                    transition={{ duration: 1, repeat: result?.status === 'running' ? Infinity : 0, ease: "linear" }}
                                  >
                                    <StatusIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${getStatusColor(result?.status || 'pending')}`} />
                                  </motion.div>
                                  <h4 className="font-semibold text-gray-900 text-xs sm:text-sm flex-1 truncate">{testCase.test_name}</h4>
                                  <span className={`px-2 py-1 text-xs rounded-full border font-medium ${getPriorityColor(testCase.priority)}`}>
                                    {testCase.priority || 'medium'}
                                  </span>
                                </div>
                                
                                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 leading-relaxed line-clamp-2">{testCase.description}</p>
                                
                                <div className="flex items-center justify-between text-xs">
                                  <div className="flex items-center space-x-2 sm:space-x-3">
                                    <span className={`px-2 py-1 rounded-lg font-medium ${
                                      testCase.category === 'Command-based'
                                        ? 'bg-orange-100 text-orange-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {testCase.category}
                                    </span>
                                    <span className="text-gray-500">
                                      {testCase.steps?.length || 0} steps
                                    </span>
                                  </div>
                                  {result && (
                                    <span className={`font-semibold ${getStatusColor(result.status)}`}>
                                      {result.status} {result.execution_time && `(${result.execution_time}s)`}
                                    </span>
                                  )}
                                </div>
                                
                                <AnimatePresence>
                                  {showTestDetails && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200"
                                    >
                                      <p className="text-xs text-gray-600 mb-2 font-medium">Test Steps:</p>
                                      <ul className="text-xs text-gray-500 space-y-1">
                                        {testCase.steps?.slice(0, 3).map((step, i) => (
                                          <li key={i} className="flex items-start space-x-2">
                                            <span className={`w-1 h-1 rounded-full mt-2 flex-shrink-0 ${
                                              testMode === 'command' ? 'bg-orange-400' : 'bg-blue-400'
                                            }`}></span>
                                            <span className="line-clamp-2">{step}</span>
                                          </li>
                                        ))}
                                        {testCase.steps?.length > 3 && (
                                          <li className={`font-medium ${
                                            testMode === 'command' ? 'text-orange-600' : 'text-blue-600'
                                          }`}>
                                            ... and {testCase.steps.length - 3} more steps
                                          </li>
                                        )}
                                      </ul>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                              
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRunSingleTest(testCase)
                                }}
                                disabled={executionState === 'running' || executionState === 'generating' || !isConnected || !isAuthenticated}
                                className={`ml-2 sm:ml-4 p-2 sm:p-3 text-gray-400 rounded-lg sm:rounded-xl transition-all duration-200 disabled:opacity-50 ${
                                  testMode === 'command'
                                    ? 'hover:text-orange-600 hover:bg-orange-50'
                                    : 'hover:text-blue-600 hover:bg-blue-50'
                                }`}
                                title="Run this test"
                              >
                                <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                              </motion.button>
                            </div>
                          </motion.div>
                        )
                      })
                    )}
                  </div>
                  
                  {/* Test Control Buttons */}
                  {testCases.length > 0 && (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 pt-6 border-t border-gray-200"
                    >
                      <div className="space-y-3">
                        <Button
                          onClick={handleRunAllTests}
                          disabled={executionState === 'running' || executionState === 'generating' || !isConnected || !isAuthenticated}
                          className={`w-full h-14 text-base font-bold shadow-lg hover:shadow-xl transition-all duration-300 ${
                            testMode === 'command'
                              ? 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700'
                              : 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700'
                          }`}
                          variant="success"
                        >
                          {executionState === 'running' ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <RefreshCw className="h-6 w-6 mr-3" />
                              </motion.div>
                              Running Tests...
                            </>
                          ) : (
                            <>
                              <Play className="h-6 w-6 mr-3" />
                              Run All Tests ({testCases.length})
                            </>
                          )}
                        </Button>
                        
                        <div className="flex space-x-3">
                          {executionState === 'running' && (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex-1"
  >
    <Button
      onClick={handleStopExecution}
      disabled={false} // ✅ NEVER disable - allow retries
      variant="danger"
      className={`w-full h-12 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 relative ${
        isStoppingExecution ? 'opacity-75' : ''
      }`}
    >
      {isStoppingExecution ? (
        <>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <RefreshCw className="h-5 w-5 mr-2" />
          </motion.div>
          Stopping... (Click to force)
          <span className="absolute top-0 right-0 h-2 w-2 bg-yellow-400 rounded-full animate-pulse" />
        </>
      ) : (
        <>
          <Square className="h-5 w-5 mr-2" />
          Stop Tests
        </>
      )}
    </Button>
    
    {/* ✅ ADDED: Manual reset option */}
    {isStoppingExecution && (
      <Button
        onClick={() => {
          setIsStoppingExecution(false)
          toast.success('Stop flag reset')
        }}
        variant="outline"
        size="sm"
        className="ml-2 mt-2 text-xs"
      >
        Reset Stop Flag
      </Button>
    )}
  </motion.div>
)}
                          
                          {testResults.length > 0 && (
                            <>
                              <Button
                                onClick={handleClearResults}
                                variant="outline"
                                className="flex-1 border-2 hover:border-gray-400 h-12"
                              >
                                Clear Results
                              </Button>
                              <Button
                                onClick={handleExportResults}
                                variant="outline"
                                className={`flex-1 border-2 h-12 ${
                                  testMode === 'command'
                                    ? 'hover:border-orange-400 hover:text-orange-700'
                                    : 'hover:border-blue-400 hover:text-blue-700'
                                }`}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Export
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Middle Panel - Test Execution & Progress */}
            <div className="xl:col-span-5 space-y-6">
              {/* Progress Bar */}
              {executionState === 'running' && progress.total > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/70 shadow-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <Activity className="h-5 w-5 mr-2 text-blue-600" />
                      </motion.div>
                      Test Progress
                      {testMode === 'command' && (
                        <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                          Command Mode
                        </span>
                      )}
                    </h3>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                      testMode === 'command'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {progress.current}/{progress.total} tests
                    </span>
                  </div>
                  <ProgressBar
                    current={progress.current}
                    total={progress.total}
                    className="mb-4"
                  />
                  {currentTest && (
                    <motion.div 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex items-center space-x-3 rounded-xl p-3 ${
                        testMode === 'command'
                          ? 'bg-orange-50'
                          : 'bg-blue-50'
                      }`}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className={`w-3 h-3 rounded-full ${
                          testMode === 'command' ? 'bg-orange-500' : 'bg-blue-500'
                        }`}
                      />
                      <p className={`text-sm ${
                        testMode === 'command' ? 'text-orange-800' : 'text-blue-800'
                      }`}>
                        Currently running: <span className="font-semibold">{currentTest}</span>
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Execution Log */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/70 shadow-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    Execution Log
                    {testMode === 'command' && (
                      <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                        Command Steps
                      </span>
                    )}
                  </h3>
                  {executionLog.length > 0 && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                      {executionLog.length} steps
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  <StepViewer steps={executionLog} />
                </div>
              </motion.div>
            </div>

            {/* Right Panel - Screenshots & Results */}
            <div className="xl:col-span-3 space-y-6">
              {/* Screenshots Gallery */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/70 shadow-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <Image className="h-5 w-5 mr-2 text-blue-600" />
                    Screenshots
                  </h3>
                  {screenshots.length > 0 && (
                    <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                      {screenshots.length} images
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  <ScreenshotGallery screenshots={screenshots} />
                </div>
              </motion.div>

              {/* Test Results Summary */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/70 shadow-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-emerald-600" />
                    Test Results
                    {testMode === 'command' && (
                      <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                        Command Results
                      </span>
                    )}
                  </h3>
                  {testResults.length > 0 && (
                    <div className="text-xs bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full font-medium">
                      {testResults.filter(r => r.status === 'passed').length}/
                      {testResults.length} passed
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  {testResults.length === 0 ? (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center text-gray-500 py-8"
                    >
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                          testMode === 'command'
                            ? 'bg-gradient-to-r from-orange-100 to-amber-100'
                            : 'bg-gradient-to-r from-green-100 to-emerald-100'
                        }`}
                      >
                        {testMode === 'command' ? (
                          <Terminal className="h-8 w-8 text-orange-500" />
                        ) : (
                          <Target className="h-8 w-8 text-green-500" />
                        )}
                      </motion.div>
                      <p className="font-semibold mb-1">No results yet</p>
                      <p className="text-xs text-gray-400">Test results will appear here</p>
                    </motion.div>
                  ) : (
                    <>
                      {/* Results Summary Cards */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-4 text-center"
                        >
                          <div className="text-2xl font-bold text-emerald-700">
                            {testResults.filter(r => r.status === 'passed').length}
                          </div>
                          <div className="text-xs text-emerald-600 font-medium">Passed</div>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl p-4 text-center"
                        >
                          <div className="text-2xl font-bold text-red-700">
                            {testResults.filter(r => r.status === 'failed').length}
                          </div>
                          <div className="text-xs text-red-600 font-medium">Failed</div>
                        </motion.div>
                      </div>
                      
                      {/* Detailed Results */}
                      <div className="space-y-3">
                        <TestResults results={testResults} />
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AutomationMode